<template>
  <fieldset
    :class="[
      $blockClass(blockClass, 'swatches')
    ]"
  >
    <legend
      class="visually-hidden"
      v-text="$string('accessibility.product.swatch_label')"
    />

    <ul
      v-if="render"
      class="product-swatches"
    >
      <li
        v-for="(object,index) of filteredArray"
        :key="`object-${object?.product?.handle}-${object?.swatch?.handle}-${cardIndex}`"
        class="product-swatch"
        :class="[
          $blockClass(blockClass, 'swatch')
        ]"
      >
        <input
          :id="getId(object)"
          class="product-swatch__input visually-hidden"
          :class="{ 'product-swatch__input--unavailable': !isAvailable(object) }"
          autocomplete="off"
          :checked="isActive(object)"
          :name="inputName"
          type="radio"
          :value="object?.swatch?.handle"
          @input="handleInput(object)"
          @focusin="handleMouseEnter(object?.swatch?.name, optionName)"
          @focusout="handleMouseLeave"
        >

        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
        <label
          class="product-swatch__label"
          :for="getId(object)"
          :style="`background-image: url(${object?.swatch?.src})`"
          :title="object?.swatch?.name.split('&apos\;').join('\'')"
          @focusin="handleMouseEnter(object?.swatch?.name, optionName)"
          @focusout="handleMouseLeave"
          @mouseenter="handleMouseEnter(object?.swatch?.name, optionName)"
          @mouseleave="handleMouseLeave"
        >
          <span
            class="visually-hidden"
            v-text="object?.swatch?.name"
          />
        </label>

        <span
          v-if="hasBadge && source === 'pdpDetail'"
          class="product-swatch__badge font-family-freight"
          v-text="badgeMap[index]"
          />
      </li>

      <li
        v-if="swatchLink && productLink.length"
        class="product-swatch__link"
      >
        <a
          :href="productLink"
          class="product-swatches__link text-body-2xs-regular"
          v-text="`+${swatchDifference}`"
        />
      </li>
    </ul>

    <span
      v-if="productForm"
      class="product-swatches__active-name text-body-2xs-regular"
      v-text="label"
    />
  </fieldset>
</template>

<script>

/**
 * Vue: Product swatches (product-swatches)
 * -----------------------------------------------------------------------------
 * Product color swatches.
 *
 * @param {Object} activeMerge - Active group object.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number} [cardIndex] - Index of parent product card.
 * @param {String} [label] - Label passed from product form.
 * @param {Array} [options] - Product options used to determine if legacy swatch
 * is unavailable.
 * @param {Boolean} [productForm] - Whether this is in a product form.
 * @param {Boolean|Object} product - Current product.
 * @param {String} optionName - Swatch Name.
 */
import { mapState } from 'vuex'
import { formatProduct } from '~/format/format-product'
import productQuery from '~graphql/product.gql'

export default {
  name: 'ProductSwatches',

  props: {
    productLink: {
      type: String,
      default: '',
    },
    cardLayout: {
      type: String,
      default: 'regular',
    },
    activeMerge: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    blockClass: {
      type: String,
      default: '',
    },
    cardIndex: {
      type: Number,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    optionName: {
      type: String,
      default: '',
    },
    productForm: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    isDickeyBundle: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: '',
    },
  },

  emits: {
    'update-merge': (payload) => typeof payload === 'object',
    'update-product-handle': (payload) => typeof payload === 'string',
    'update-product-id': (payload) => ['number', 'string'].includes(typeof payload),
    'swatch-mouse-enter': (payload) => typeof payload === 'object',
    'swatch-mouse-leave': () => true,
  },

  data() {
    return {
      badgeMap: [],
      cnvsGlobal: true,
      initialHandle: this.product?.handle,
      swatchDifference: 0,
      swatchLimitDesktop: this.$variable('settings.swatchLimit.desktop'),
      swatchLimitDesktopLarge: this.$variable('settings.swatchLimit.desktopLarge'),
      swatchLimitRegular: this.$variable('settings.swatchLimit.regular'),
      swatchLimitSmall: this.$variable('settings.swatchLimit.small'),
      swatchLink: false,
      hasBadge: false,
      badgeData: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute all grouping and extension objects.
     * - Only output in product form to avoid query overhead on product cards.
     * - Outputs array of objects where each object represents a product
     *   extension group with its associated merge objects.
     * @returns {Array<Object>}
     */
    allMerges() {
      if (
        !this.product?.extensions?.length ||
        !this.productForm
      ) {
        return []
      }

      /**
       * Map all objects.
       */
      const extensionGrouping = this.product?.extensions.map((object) => object.extensionGrouping)
      const groupingExtensions = this.product?.grouping.map((object) => object.groupingExtensions)

      /**
       * Flatten and filter out objects which shouldn't be rendered.
       */
      const allObjects = [...extensionGrouping, ...groupingExtensions]
        .flat()
        .filter((object) => object?.render)

      /**
       * Create array of extension metaobject grouped merge objects.
       */
      const allMerges = []

      allObjects.forEach((object) => {
        const handle = object.metaobject?.extension || object.swatch?.handle

        const mergeObject = allMerges.find((findObject) => {
          return findObject.handle === handle
        })

        if (mergeObject) {
          const alreadyExists = mergeObject.objects.find((findObject) => {
            return findObject.swatch?.handle === object.swatch?.handle
          })

          if (!alreadyExists) {
            mergeObject.objects.push(object)
            return
          }

          return
        }

        allMerges.push({
          handle,
          objects: [object],
        })
      })

      return allMerges
    },

    /**
     * Compute de-duplicated objects.
     * - Take all objects and filter so that only one swatch from the active
     *   grouping is shown.
     * - Otherwise use first object in each merge metaobject.
     * @returns {Array<Object>}
     */
    deDuplicatedObjects() {
      if (
        !this.product?.extensions?.length ||
        !this.productForm
      ) {
        return []
      }

      return this.allMerges.map((merge) => {
        const match = merge.objects.find((object) => {
          return object.metaobject?.grouping === this.activeMerge.metaobject?.grouping
        })

        if (match) {
          return match
        }

        return merge.objects[0]
      })
    },

    /**
     * Compute array used for swatches.
     * - Filters swatches which shouldn't be rendered.
     * @returns {Array<Object>}
     */
    filteredArray() {
      if (this.deDuplicatedObjects?.length) {
        return this.reduceSwatches(this.deDuplicatedObjects)
      }

      if (this.product?.grouping?.length) {
        return this.reduceSwatches(this.product?.grouping.filter((object) => object.render))
      }

      if (this.product?.legacySwatches?.length) {
        return this.reduceSwatches(this.product?.legacySwatches.filter(this.isRendered))
      }

      if (this.isDickeyBundle && this.product?.currentSwatch[0]?.swatch?.name) {
        return this.product?.currentSwatch
      }

      return []
    },

    /**
     * Compute input name.
     * - Name attribute should be the same between all inputs but unique per
     *   product when on the PLP.
     * @returns {String}
     */
    inputName() {
      return this.productForm
        ? `swatch-for-${this.product.handle}`
        : `swatch-for-${this.initialHandle}`
    },

    /**
     * Compute whether or not to render product groups.
     * @returns {Boolean}
     */
    render() {
      return Boolean(this.filteredArray.length || this.productForm)
    },
  },

  async mounted() {
    if (this.source === 'pdpDetail') {
      for (const swatch of this.filteredArray) {
        await this.fetchSwatchBadge(swatch.product.handle)
      }
    }
  },
  watch: {

    /**
     * Watch active group to update local v-model handle.
     */
    activeGroup: {
      handler() {
        if (!this.activeGroup?.swatch?.handle) {
          return
        }

        this.localModelValue = this.activeGroup?.swatch?.handle
      },
      immediate: true,
    },

    badgeMap: {
      handler() {
        this.showBadgeOnChange()
      },
      deep: true,
    },
  },

  methods: {

    /**
     * Compute id to use for this swatch input.
     * @param {Object} object - Group/swatch.
     * @returns {String}
     */
    getId(object) {
      return this.productForm
        ? `${object?.product.handle}-${object?.swatch?.handle}-product-form`
        : `${this.initialHandle}-${object?.product.handle}-${object?.swatch?.handle}`
    },

    /**
     * Get if the object should be rendered.
     * @param {Object} object - Group/swatch.
     * @returns {Boolean}
     */
    isRendered(object) {
      if (object.type === 'legacy' && this.productForm) {
        return this.isAvailable(object) || !object.finalSale
      }

      return object.render
    },

    /**
     * Get if the object is available.
     * @param {Object} object - Group/swatch.
     * @returns {Boolean}
     */
    isAvailable(object) {
      if (object.type !== 'legacy') {
        return object.product.available
      }

      if (this.productForm) {
        return !this.options[0]?.disabled[object.swatch.name]
      }

      return this.product?.variants
        .filter((variant) => variant.selectedOptions[0]?.value === object.swatch.name)
        .some((variant) => variant.available)
    },

    /**
     * Handle group input change
     * @param {Object} object - Group/swatch.
     */
    handleInput(object) {
      if (object?.product.handle && object?.product.handle !== this.product?.handle) {
        if (this.productForm) {
          this.$emit('update-product-handle', object.product.handle)
        } else {
          this.$emit('update-product-id', object.product.id)
        }
      }

      this.$emit('update-merge', object)
    },

    /**
     * Check if group is active.
     * @param {Object} object - Group/swatch.
     * @returns {Boolean}
     */
    isActive(object) {
      if (
        this.activeMerge &&
        this.activeMerge.swatch?.handle === object.swatch?.handle &&
        this.product?.handle === object.product?.handle
      ) {
        return true
      }

      return false
    },

    /**
     * Reduce swatches to limit.
     * @param {Array} swatches - Swatches to reduce.
     * @returns {Array}
     */
    reduceSwatches(swatches) {
      if (this.productForm) {
        return swatches
      }

      let swatchesMaxLength = swatches.length

      if (this.isMobile) {
        if (this.cardLayout === 'regular') {
          swatchesMaxLength = this.swatchLimitRegular
        } else {
          swatchesMaxLength = this.swatchLimitSmall
        }
      } else if (this.cardLayout === 'large') {
        swatchesMaxLength = this.swatchLimitDesktopLarge
      } else {
        swatchesMaxLength = this.swatchLimitDesktop
      }

      if (swatchesMaxLength < swatches.length) {
        this.swatchLink = true
        this.swatchDifference = swatches.length - swatchesMaxLength
      } else {
        this.swatchLink = false
        this.swatchDifference = 0
      }

      return [...swatches.slice(0, swatchesMaxLength)]
    },

    /**
     * @param {String} swatchName swatch name
     * @param {String} swatchHandle swatch handle
     */
    handleMouseEnter(swatchName, swatchHandle) {
      this.$emit(
        'swatch-mouse-enter',
        {
          swatchName,
          swatchHandle,
        },
      )
    },

    /**
     * Handle mouse leave event.
     */
    handleMouseLeave() {
      this.$emit('swatch-mouse-leave')
    },

    async fetchSwatchBadge(handle) {
      if (handle === false) {
        return
      }

      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: {
            country: this.$variable('store.country'),
            handle,
            language: this.$variable('store.language'),
          },
        })

        const formattedProduct = formatProduct(response)
        this.hasBadge = formattedProduct.collectionsMetafields?.some(
          metafield => metafield && metafield.value && metafield.value.trim() !== '',
        )
        this.badgeText = formattedProduct.collectionsMetafields?.find(
          metafield => metafield && metafield.value && metafield.value.trim() !== '',
        )?.value || ''

        this.badgeMap.push(this.badgeText)

        if (this.badgeData) {
          this.hasBadge = true
        }

      } catch (error) {
        cnvs.ReportError('Failed to load product', error)
      }
    },

    async showBadgeOnChange() {
      await this.$nextTick()
      const badgeContainers = document.querySelectorAll('.product-swatch__badge')
      badgeContainers.forEach(el => {
        el.style.display = 'block'
      })
    },
  },
}
</script>
